import { getHrefFromProps } from '@components/INLink/getHrefFromProps'
import { isCrossingBetweenRouters } from '@components/INLink/isCrossingBetweenRouters'
import { INLinkProps } from '@components/INLink/types'
import { useRouterMethod } from '@components/INLink/useRouterMethod'
import { ArtificialLink } from '@internations/ds'
import { useNextRouterType } from '@routes/utils/useINRouter'
import { useIsSpaAllowedAccordingToExperiment } from '@routes/utils/useIsSpaAllowedAccordingToExperiment'
import { logError } from '@util/logger'
import { ReactElement, Ref, forwardRef } from 'react'

// This is the only place in the entire repository that we should be importing NextLink!
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link'

// This needs to be imported (nearly) last, otherwise Jest encounters a circular dependencies on many tests
import { useIsNewerBuildAvailable } from '@data/store/slices/buildStatus/getters'

/**
 * Links to any URL while automatically using NextLink when applicable.
 *
 * Requires a `route` (TRoute), `externalUrl` (string), or `onClick` function. Also accepts all props that NextLink and MUI's Link will accept.
 */
export const INLink = forwardRef(function INLinkWithRef(
    props: INLinkProps,
    ref?: Ref<HTMLAnchorElement>
): ReactElement | null {
    const {
        route,
        params,
        externalUrl,
        onClick,
        children,
        underline = 'hover',
        passHref = true,
        debug,
        ...anchorProps
    } = props
    const href = getHrefFromProps({ route, params, externalUrl, debug })
    // We don't want to run the experiment query hook if we couldn't determine the href
    const isAllowedByExperiment = useIsSpaAllowedAccordingToExperiment(route)
    const isNewerBuildAvailable = useIsNewerBuildAvailable()
    const routerMethod = useRouterMethod(route)
    const routerType = useNextRouterType()

    if (!href && !onClick) {
        logError(`Unable to determine which route to use with INLink/INButton/INButtonLink`, {
            metadata: {
                route,
                params,
                externalUrl,
            },
        })

        return null
    }

    const commonProps = {
        ...anchorProps,
        href,
        underline,
        onClick,
        ref,
    }

    const canUseNextLink =
        Boolean(href) &&
        !isNewerBuildAvailable &&
        isAllowedByExperiment &&
        !commonProps.download &&
        (routerMethod === 'push' || routerMethod === 'replace')

    if (debug) {
        console.log(`<INLink> href=${href} `, {
            canUseNextLink,
            isNewerBuildAvailable,
            isAllowedByExperiment,
            routerMethod,
            isCrossingBetweenRouters: isCrossingBetweenRouters(routerType, route),
            prefetch: isCrossingBetweenRouters(routerType, route) ? false : null,
            routerType,
            commonProps,
        })
    }

    if (!canUseNextLink) {
        if (typeof commonProps.prefetch === 'boolean') {
            // For HTML this attribute needs to have a string value
            // @ts-expect-error TS thinks we should only assign a boolean to `prefetch`
            commonProps.prefetch = commonProps.prefetch ? 'true' : 'false'
        }

        // Remove NextLink props that are not standard HTML attributes
        if (typeof commonProps.shallow === 'boolean') {
            delete commonProps.shallow
        }

        if (typeof commonProps.scroll === 'boolean') {
            delete commonProps.scroll
        }

        return <ArtificialLink {...commonProps}>{children}</ArtificialLink>
    }

    // When we're on an App Router screen and a `<Link>` points to a Pages Router screen, Next will try prefetching
    // a .txt file for that route, which results in a 404. To avoid this, we set `prefetch=false` if the link will
    // point to a different router.
    const prefetch = isCrossingBetweenRouters(routerType, route) ? false : null

    return (
        <ArtificialLink
            shallow={routerMethod === 'replace'}
            passHref={passHref}
            component={NextLink}
            prefetch={prefetch}
            {...commonProps}
        >
            {children}
        </ArtificialLink>
    )
})
